import React, { useState } from "react";
import { Star, ChevronRight, Youtube } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import type { TopInfluencer } from "../../types/analytics.types";

export type { TopInfluencer };

export interface TopInfluencersTableProps {
  data: TopInfluencer[];
  title?: string;
  subtitle?: string;
  info?: string;
  showViewAll?: boolean;
  onViewAll?: () => void;
  isLoading?: boolean;
}

const SKELETON_ROWS = 5;
const COLUMN_COUNT = 8;

export const TopInfluencersTable: React.FC<TopInfluencersTableProps> = ({
  data,
  title = "Top Performing Influencers",
  subtitle = "Ranks creators by CPE — who drove the most engagement per rupee spent.",
  info = "Ranks creators by CPE — who drove the most engagement per rupee spent.",
  showViewAll = true,
  onViewAll,
  isLoading = false,
}) => {
  const [tooltipOpen, setTooltipOpen] = useState(false);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div className="flex items-center gap-3.5">
          <div className="w-12 h-12 rounded-xl bg-purple-50 flex items-center justify-center shrink-0">
            <Star className="w-6 h-6 text-purple-600" strokeWidth={1.8} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold text-gray-900">{title}</h2>
              {info && (
                <div
                  className="relative inline-flex items-center"
                  onMouseEnter={() => setTooltipOpen(true)}
                  onMouseLeave={() => setTooltipOpen(false)}
                >
                  <div
                    className="w-4 h-4 rounded-full border border-gray-400 text-gray-400 hover:text-gray-600 hover:border-gray-600 flex items-center justify-center transition-colors shrink-0 cursor-pointer"
                    aria-label="More information"
                  >
                    <span className="text-[10px] font-bold leading-none select-none">i</span>
                  </div>

                  {tooltipOpen && (
                    <div className="absolute left-0 top-6 z-50 w-64 bg-gray-900 text-white text-xs rounded-xl px-3 py-2.5 shadow-xl leading-relaxed">
                      <div className="absolute -top-1.5 left-2 w-3 h-3 bg-gray-900 rotate-45 rounded-sm" />
                      <span className="relative z-10">{info}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
            {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
          </div>
        </div>

        {showViewAll && (
          <Button
            variant="outline"
            type="button"
            onClick={onViewAll}
          >
            <span>View all Influencers</span>
            <ChevronRight className="w-4 h-4" />
          </Button>
        )}
      </div>

      <div className="overflow-x-auto rounded-t-xl">
        <table className="w-full">
          <thead className="bg-pink-50">
            <tr className="border-b border-pink-100 text-gray-900 text-sm">
              <th className="text-left py-3 px-4 font-semibold rounded-tl-xl">
                Influencer
              </th>
              <th className="text-center py-3 px-4 font-semibold">
                Platform
              </th>
              <th className="text-center py-3 px-4 font-semibold">
                Followers
              </th>
              <th className="text-center py-3 px-4 font-semibold">
                Total Views
              </th>
              <th className="text-left py-3 px-4 font-semibold">
                Avg ER%
              </th>
              <th className="text-left py-3 px-4 font-semibold">
                Cost
              </th>
              <th className="text-left py-3 px-4 font-semibold">
                CPV
              </th>
              <th className="text-left py-3 px-4 font-semibold rounded-tr-xl">
                CPE
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading &&
              Array.from({ length: SKELETON_ROWS }).map((_, i) => (
                <tr key={`skeleton-${i}`} className="border-b border-gray-100">
                  <td className="py-3 px-4">
                    <div className="flex items-center space-x-3">
                      <Skeleton className="w-8 h-8 rounded-full" />
                      <Skeleton className="h-4 w-24" />
                    </div>
                  </td>
                  {Array.from({ length: COLUMN_COUNT - 1 }).map((_, j) => (
                    <td key={j} className="py-3 px-4">
                      <Skeleton className="h-4 w-12" />
                    </td>
                  ))}
                </tr>
              ))}

            {!isLoading && data.length === 0 && (
              <tr>
                <td
                  colSpan={COLUMN_COUNT}
                  className="py-12 text-center text-gray-500"
                >
                  No influencer data available for this campaign.
                </td>
              </tr>
            )}

            {!isLoading &&
              data.map((influencer, index) => (
                <tr
                  key={index}
                  className="border-b border-gray-100 hover:bg-gray-50"
                >
                  <td className="py-3 px-4">
                    <div className="flex items-center space-x-3">
                      {influencer.avatar && (influencer.avatar.startsWith('http') || influencer.avatar.startsWith('/') || influencer.avatar.startsWith('data:')) ? (
                        <img
                          src={influencer.avatar}
                          alt={influencer.name}
                          className="w-8 h-8 rounded-full object-cover"
                          onError={(e) => {
                            (e.target as HTMLElement).style.display = 'none';
                          }}
                        />
                      ) : (
                        <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-blue-500 rounded-full flex items-center justify-center shrink-0">
                          <span className="text-white text-sm font-semibold">
                            {influencer.name
                              .split(" ")
                              .map((n) => n[0])
                              .join("")
                              .slice(0, 2)
                              .toUpperCase()}
                          </span>
                        </div>
                      )}
                      <span className="font-medium text-gray-900">
                        {influencer.name}
                      </span>
                    </div>
                  </td>

                  {/* Platform */}
                  <td className="py-3 px-4">
                    <div className="flex items-center justify-center">
                      {influencer.platform?.toLowerCase() === 'youtube' ? (
                        <Youtube className="w-5 h-5 text-red-500" />
                      ) : influencer.platform && influencer.platform.toLowerCase() !== 'instagram' ? (
                        <Badge variant="outline">{influencer.platform}</Badge>
                      ) : (
                        <img
                          src="/icons/instagram-logos/GradientGlyph/Instagram_Glyph_Gradient.png"
                          alt="Instagram"
                          className="w-5 h-5"
                        />
                      )}
                    </div>
                  </td>

                  {/* Followers */}
                  <td className="py-3 px-4 text-gray-600 text-center">
                    {influencer.followers ?? "-"}
                  </td>

                  {/* Total Views */}
                  <td className="py-3 px-4 text-gray-600 text-center">
                    {influencer.views}
                  </td>

                  {/* Avg ER% */}
                  <td className="py-3 px-4 text-gray-600">
                    {influencer.engagement}
                  </td>

                  {/* Cost */}
                  <td className="py-3 px-4 text-gray-600">
                    {influencer.cost ?? "-"}
                  </td>

                  {/* CPV */}
                  <td className="py-3 px-4 text-gray-600">
                    {influencer.cpv ?? "-"}
                  </td>

                  {/* CPE */}
                  <td className="py-3 px-4 text-gray-600">
                    {influencer.cpe ?? "-"}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TopInfluencersTable;
